import { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axios.config';
import { demoAPI } from '../utils/demoMode';
import { BACKEND_CONFIG, DEMO_MODE_CONFIG } from '../utils/productionConfig';

const API_URL = BACKEND_CONFIG.apiUrl;
const USE_DEMO_MODE = DEMO_MODE_CONFIG.shouldUseDemoMode;

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: 'user' | 'marketing' | 'admin';
  isPhoneVerified: boolean;
  isEmailVerified: boolean;
  profileCompleted: boolean;
  subscription?: any;
  adminVerification?: {
    status: 'pending' | 'approved' | 'rejected';
    rejectionReason?: string;
  };
}

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

export const useAuth = () => {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: localStorage.getItem('token'),
    loading: true,
    error: null
  });
  
  // Check for valid demo mode on initialization
  useEffect(() => {
    const token = localStorage.getItem('token');
    const demoMode = localStorage.getItem('demoMode');
    
    // If demo mode is NOT enabled but we have a demoMode flag, clear it
    if (!USE_DEMO_MODE && demoMode === 'true') {
      console.log('🧹 Clearing old demo mode data...');
      localStorage.removeItem('token');
      localStorage.removeItem('demoMode');
      setState(prev => ({
        ...prev,
        token: null,
        user: null,
        loading: false
      }));
      return;
    }
    
    // If we have a token but no demo mode flag and demo mode is required, validate it
    if (token && !demoMode && USE_DEMO_MODE) {
      console.log('🧹 Demo mode enabled but token is not from demo mode, clearing...');
      localStorage.removeItem('token');
      setState(prev => ({
        ...prev,
        token: null,
        user: null,
        loading: false
      }));
      return;
    }
  }, []);

  // Set axios default header
  useEffect(() => {
    if (state.token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [state.token]);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setState(prev => ({ ...prev, loading: false, user: null, token: null }));
      return { success: false };
    }

    // Check if in demo mode
    if (localStorage.getItem('demoMode') === 'true' || USE_DEMO_MODE) {
      try {
        const result = await demoAPI.loadUser(token);
        
        if (result.success && result.data) {
          setState(prev => ({
            ...prev,
            user: result.data.user as any,
            loading: false,
            error: null
          }));
          return { success: true };
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('demoMode');
          setState({
            user: null,
            token: null,
            loading: false,
            error: null
          });
          return { success: false };
        }
      } catch (_error) {
        localStorage.removeItem('token');
        localStorage.removeItem('demoMode');
        setState({
          user: null,
          token: null,
          loading: false,
          error: null
        });
        return { success: false };
      }
    }

    // Regular backend loadUser
    try {
      const { data } = await axios.get(`${API_URL}/auth/me`);
      
      // Handle different response structures safely
      const user = data?.data?.user || data?.user || null;
      
      if (!user) {
        throw new Error('No user data in response');
      }
      
      setState(prev => ({
        ...prev,
        user: user,
        loading: false,
        error: null
      }));
      return { success: true };
    } catch (error: any) {
      // Silently handle errors - Only clear token if it exists and we got an auth error
      const shouldClearToken = (error.response?.status === 401 || error.response?.status === 403) && token;
      
      if (shouldClearToken) {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
      }
      
      setState({
        user: null,
        token: null,
        loading: false,
        error: null
      });
      return { success: false };
    }
  }, []);

  // Load user on mount
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const register = async (userData: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
    role?: string;
  }) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      const response = await axios.post(`${API_URL}/auth/register`, userData);
      
      // Check if response has expected structure
      if (!response.data) {
        throw new Error('No data in response');
      }
      
      if (!response.data.data) {
        throw new Error('Invalid response structure from server');
      }
      
      const { token, user } = response.data.data;
      
      if (!token) {
        throw new Error('No token in response');
      }
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Notify Expo app that user has logged in (after registration)
      if (window.ReactNativeWebView) {
        console.log('📱 [Auth] Sending USER_LOGGED_IN to Expo app (Registration)...');
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'USER_LOGGED_IN',
          token: token
        }));
      }
      
      setState({
        user: user,
        token: token,
        loading: false,
        error: null
      });
      
      return { success: true, data: response.data.data };
    } catch (error: any) {
      let errorMsg = 'Registration failed';
      
      if (error.message === 'Network Error') {
        errorMsg = `Cannot connect to server at ${API_URL}. Please ensure:\n1. Backend server is running on port 5001\n2. Run: cd backend && npm start\n3. Check CORS configuration`;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMsg
      }));
      return { success: false, error: errorMsg };
    }
  };

  const login = async (email: string, password: string) => {
    // Use demo mode if enabled
    if (USE_DEMO_MODE) {
      try {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        const result = await demoAPI.login(email, password);
        
        if (result.success && result.data) {
          const { token, user } = result.data;
          
          localStorage.setItem('token', token);
          localStorage.setItem('demoMode', 'true');
          
          setState({
            user: user as any,
            token: token,
            loading: false,
            error: null
          });
          
          return { success: true, data: result.data };
        } else {
          setState(prev => ({
            ...prev,
            loading: false,
            error: result.message || 'Invalid email or password'
          }));
          return { success: false, error: result.message || 'Invalid email or password' };
        }
      } catch (error: any) {
        setState(prev => ({
          ...prev,
          loading: false,
          error: 'Invalid email or password'
        }));
        return { success: false, error: 'Invalid email or password' };
      }
    }

    // Regular backend login
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password
      });
      
      // Check if response is HTML (backend not accessible)
      if (typeof response.data === 'string' && response.data.trim().startsWith('<!DOCTYPE')) {
        throw new Error('Backend server not accessible. Please configure VITE_BACKEND_URL or run backend locally.');
      }
      
      // Check if response has expected structure
      if (!response.data) {
        throw new Error('No data in response');
      }
      
      if (!response.data.data) {
        throw new Error('Invalid response structure from server');
      }
      
      const { token, user } = response.data.data;
      
      if (!token) {
        throw new Error('No token in response');
      }
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Notify Expo app that user has logged in
      if (window.ReactNativeWebView) {
        console.log('📱 [Auth] Sending USER_LOGGED_IN to Expo app...');
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'USER_LOGGED_IN',
          token: token
        }));
      }
      
      setState({
        user: user,
        token: token,
        loading: false,
        error: null
      });
      
      return { success: true, data: response.data.data };
    } catch (error: any) {
      // Check for 401 Unauthorized (wrong credentials)
      if (error.response?.status === 401) {
        const errorMsg = error.response?.data?.message || 'Invalid email or password';
        setState(prev => ({
          ...prev,
          loading: false,
          error: errorMsg
        }));
        return { success: false, error: errorMsg };
      }
      
      // Check for 400 Bad Request (wrong credentials or validation error)
      if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.message || 'Invalid email or password';
        setState(prev => ({
          ...prev,
          loading: false,
          error: errorMsg
        }));
        return { success: false, error: errorMsg };
      }
      
      // Check if this is a 404 error (backend not running)
      if (error.response?.status === 404 || error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        // Silently try demo mode login - don't log warning unless it fails
        
        // Try demo mode login
        try {
          const result = await demoAPI.login(email, password);
          
          if (result.success && result.data) {
            const { token, user } = result.data;
            
            localStorage.setItem('token', token);
            localStorage.setItem('demoMode', 'true');
            
            setState({
              user: user as any,
              token: token,
              loading: false,
              error: null
            });
            
            return { success: true, data: result.data };
          } else {
            setState(prev => ({
              ...prev,
              loading: false,
              error: result.error || 'Invalid email or password'
            }));
            return { success: false, error: result.error || 'Invalid email or password' };
          }
        } catch (demoError) {
          setState(prev => ({
            ...prev,
            loading: false,
            error: 'Invalid email or password'
          }));
          return { success: false, error: 'Invalid email or password' };
        }
      }
      
      // Handle other errors
      const errorMsg = error.response?.data?.message || error.message || 'Invalid email or password';
      
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMsg
      }));
      
      return { success: false, error: errorMsg };
    }
  };

  const truecallerLogin = async (truecallerData: {
    phone: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  }) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    // Check if we should use demo mode first
    if (USE_DEMO_MODE) {
      try {
        const result = await demoAPI.truecallerLogin(truecallerData);
        
        if (result.success && result.data) {
          const { token, user } = result.data;
          
          localStorage.setItem('token', token);
          localStorage.setItem('demoMode', 'true');
          
          setState({
            user: user as any,
            token: token,
            loading: false,
            error: null
          });
          
          return { success: true, data: result.data };
        } else {
          setState(prev => ({
            ...prev,
            loading: false,
            error: result.message || 'Truecaller login failed'
          }));
          return { success: false, error: result.message || 'Truecaller login failed' };
        }
      } catch (demoError) {
        setState(prev => ({
          ...prev,
          loading: false,
          error: 'Truecaller login failed'
        }));
        return { success: false, error: 'Truecaller login failed' };
      }
    }
    
    // Try backend first
    try {
      const { data } = await axios.post(`${API_URL}/auth/truecaller-login`, truecallerData);
      
      // Safely access nested data
      const token = data?.data?.token;
      const user = data?.data?.user;
      
      if (!token || !user) {
        throw new Error('Invalid response structure from server');
      }
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Notify Expo app that user has logged in (after Truecaller login)
      if (window.ReactNativeWebView) {
        console.log('📱 [Auth] Sending USER_LOGGED_IN to Expo app (Truecaller)...');
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'USER_LOGGED_IN',
          token: token
        }));
      }
      
      setState({
        user: user,
        token: token,
        loading: false,
        error: null
      });
      
      return { success: true, data: data.data };
    } catch (error: any) {
      // Check if backend not accessible - silently fall back to demo mode
      if (error.message === 'Network Error' || error.code === 'ERR_NETWORK' || error.response?.status === 404) {
        try {
          const result = await demoAPI.truecallerLogin(truecallerData);
          
          if (result.success && result.data) {
            const { token, user } = result.data;
            
            localStorage.setItem('token', token);
            localStorage.setItem('demoMode', 'true');
            
            setState({
              user: user as any,
              token: token,
              loading: false,
              error: null
            });
            
            return { success: true, data: result.data };
          } else {
            setState(prev => ({
              ...prev,
              loading: false,
              error: result.message || 'Truecaller login failed'
            }));
            return { success: false, error: result.message || 'Truecaller login failed' };
          }
        } catch (_demoError) {
          setState(prev => ({
            ...prev,
            loading: false,
            error: 'Truecaller login failed'
          }));
          return { success: false, error: 'Truecaller login failed' };
        }
      }
      
      // For other errors, log and return error
      const errorMsg = error.response?.data?.message || 'Truecaller login failed';
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMsg
      }));
      return { success: false, error: errorMsg };
    }
  };

  const sendOTP = async (phone?: string) => {
    try {
      const { data } = await axios.post(`${API_URL}/auth/send-otp`, { phone });
      return { success: true, data: data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to send OTP'
      };
    }
  };

  const verifyOTP = async (otp: string) => {
    try {
      const { data } = await axios.post(`${API_URL}/auth/verify-otp`, { otp });
      
      // Reload user to get updated verification status
      await loadUser();
      
      return { success: true, data: data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'OTP verification failed'
      };
    }
  };

  const sendOTPForLogin = async (phone: string) => {
    try {
      const { data } = await axios.post(`${API_URL}/auth/otp-login/send`, { phone });
      return { success: true, data: data };
    } catch (error: any) {
      
      let errorMsg = 'Failed to send OTP';
      
      if (error.message === 'Network Error') {
        errorMsg = 'Backend server not running. Please start the server: cd backend && npm start';
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      
      return {
        success: false,
        error: errorMsg
      };
    }
  };

  const verifyOTPForLogin = async (phone: string, otp: string) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      const { data } = await axios.post(`${API_URL}/auth/otp-login/verify`, { phone, otp });
      
      const token = data?.data?.token;
      const user = data?.data?.user;
      
      if (!token || !user) {
        throw new Error('Invalid response structure from server');
      }
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Notify Expo app that user has logged in
      if (window.ReactNativeWebView) {
        console.log('📱 [Auth] Sending USER_LOGGED_IN to Expo app (OTP Login)...');
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'USER_LOGGED_IN',
          token: token
        }));
      }
      
      setState({
        user: user,
        token: token,
        loading: false,
        error: null
      });
      
      return { success: true, data: data.data };
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'OTP verification failed';
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMsg
      }));
      return { success: false, error: errorMsg };
    }
  };

  const logout = useCallback(async () => {
    try {
      // Call backend logout endpoint
      await axios.post(`${API_URL}/auth/logout`);
    } catch (_error) {
      // Silently handle logout errors
    } finally {
      // Notify Expo app that user has logged out
      if (window.ReactNativeWebView) {
        console.log('📱 [Auth] Sending USER_LOGGED_OUT to Expo app...');
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'USER_LOGGED_OUT'
        }));
      }
      
      // Clear only auth-related localStorage items (not app-specific data)
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('demoMode');
      
      // Clear axios Authorization header
      delete axios.defaults.headers.common['Authorization'];
      
      // Reset state
      setState({
        user: null,
        token: null,
        loading: false,
        error: null
      });
      
      // Force redirect to home page to ensure clean logout
      window.location.href = '/';
    }
  }, []);

  return {
    user: state.user,
    token: state.token,
    loading: state.loading,
    error: state.error,
    isAuthenticated: !!state.user,
    register,
    login,
    truecallerLogin,
    sendOTP,
    verifyOTP,
    sendOTPForLogin,
    verifyOTPForLogin,
    logout,
    loadUser
  };
};